// src/components/Presentation/QRCodeGenerator.js
import React, { useState, useRef, useEffect } from 'react';
import { X, Download, Share, Copy, QrCode, Link, Users, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';

const QRCodeGenerator = ({ 
  sessionCode, 
  presentationTitle, 
  participantCount = 0,
  onClose 
}) => {
  const [copied, setCopied] = useState(false);
  const [shareMethod, setShareMethod] = useState('qr'); // 'qr' or 'link'
  const qrRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  
  const joinUrl = `${window.location.origin}/join/${sessionCode}`;
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const generateQRCode = () => {
    try {
      const container = qrRef.current;
      const svg = container ? container.querySelector('svg') : null;
      if (!svg) {
        throw new Error('QR SVG not found');
      }

      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svg);
      const encoded = encodeURIComponent(svgString);
      const dataUrl = `data:image/svg+xml;charset=utf-8,${encoded}`;
      setQrDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
      // Fallback to external service
      setQrDataUrl(
        `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(joinUrl)}`
      );
    }
  };

  useEffect(() => {
    if (shareMethod === 'qr') {
      generateQRCode();
    }
  }, [shareMethod, joinUrl]);

  const downloadQRCode = () => {
    // Fix: Add error handling for image creation
    if (!qrDataUrl) {
      console.error('No QR code data available for download');
      return;
    }
    
    const link = document.createElement('a');
    link.download = `session-${sessionCode}-qr.png`;
    link.href = qrDataUrl;
    link.click();
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join: ${presentationTitle}`,
          text: `Join my interactive presentation using code: ${sessionCode}`,
          url: joinUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Share Presentation</h2>
              <p className="text-sm text-gray-500 mt-1">
                Let your audience join the interactive session
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Session Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-900">{presentationTitle}</h3>
              {participantCount > 0 && (
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="w-4 h-4 mr-1" />
                  {participantCount}
                </div>
              )}
            </div>
            <div className="text-2xl font-mono font-bold text-indigo-600 tracking-wider">
              {sessionCode}
            </div>
          </div>

          {/* Share Method Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => setShareMethod('qr')}
              className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                shareMethod === 'qr'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <QrCode className="w-4 h-4 mr-2" />
              QR Code
            </button>
            <button
              onClick={() => setShareMethod('link')}
              className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                shareMethod === 'link'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Link className="w-4 h-4 mr-2" />
              Link
            </button>
          </div>

          {/* QR Code View */}
          {shareMethod === 'qr' && (
            <div className="text-center">
              <div 
                ref={qrRef}
                className="inline-block bg-white p-6 rounded-lg border-2 border-gray-200 mb-4"
              >
                <QRCodeSVG 
                  value={joinUrl}
                  size={200}
                  level="M"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>
              
              <div className="space-y-3">
                <p className="text-sm text-gray-600 flex items-center justify-center">
                  <Smartphone className="w-4 h-4 mr-2" />
                  Scan with your phone camera to join
                </p>
                
                <div className="flex space-x-2">
                  <button
                    onClick={downloadQRCode}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </button>
                  <button
                    onClick={shareNative}
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                  >
                    <Share className="w-4 h-4 mr-2" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Link View */}
          {shareMethod === 'link' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Join URL
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={joinUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
                  />
                  <button
                    onClick={copyToClipboard}
                    className={`px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg transition-colors ${
                      copied 
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Instructions for Participants:</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Click the link or go to <strong>{window.location.host}</strong></li>
                  <li>Enter session code: <strong>{sessionCode}</strong></li>
                  <li>Choose to join anonymously or with your name</li>
                  <li>Start participating in the interactive presentation!</li>
                </ol>
              </div>

              <button
                onClick={shareNative}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
              >
                <Share className="w-4 h-4 mr-2" />
                Share Link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
